export type CoreObservation = {
  route: string
  status: number
  ok: boolean
  capturedAt: string
  data?: unknown
  error?: string
}

export type CoreObservabilitySnapshot = {
  contract:
    'SEI_GENESIS_CORE_OBSERVABILITY_V1'

  source:
    'LIVE_SEI_CORE'

  capturedAt: string

  verified: boolean
  readOnly: true

  totalSurfaces: number
  successfulSurfaces: number
  failedSurfaces: number

  observations:
    CoreObservation[]
}

const SURFACES = [
  '/api/v1/laia/health',
  '/api/v1/laia/status',
  '/api/v1/laia/architecture',
  '/api/v1/laia/targets',
  '/api/v1/registry',
  '/api/v1/modules',
  '/api/v1/command-center/summary',
  '/api/v1/command-center/summary/readiness',
  '/api/v1/devops/status',
] as const

/*
 * Bound the amount of CORE information placed into
 * a conversational request.
 *
 * This is not synthetic summarization.
 * It only prevents unexpectedly huge payloads.
 */

const MAX_ARRAY_ITEMS = 60
const MAX_OBJECT_KEYS = 100
const MAX_STRING_LENGTH = 10000
const MAX_DEPTH = 7

function compact(
  value: unknown,
  depth = 0,
): unknown {

  if (depth > MAX_DEPTH) {
    return '[DEPTH_LIMIT]'
  }

  if (value === null) {
    return null
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value
  }

  if (typeof value === 'string') {
    if (
      value.length >
      MAX_STRING_LENGTH
    ) {
      return (
        value.slice(
          0,
          MAX_STRING_LENGTH,
        ) +
        '…[TRUNCATED]'
      )
    }

    return value
  }

  if (Array.isArray(value)) {
    return value
      .slice(
        0,
        MAX_ARRAY_ITEMS,
      )
      .map(
        item =>
          compact(
            item,
            depth + 1,
          ),
      )
  }

  if (
    typeof value === 'object' &&
    value !== null
  ) {
    return Object.fromEntries(
      Object
        .entries(
          value as Record<
            string,
            unknown
          >,
        )
        .slice(
          0,
          MAX_OBJECT_KEYS,
        )
        .map(
          ([key, child]) => [
            key,
            compact(
              child,
              depth + 1,
            ),
          ],
        ),
    )
  }

  return String(value)
}

async function readSurface(
  route: string,
): Promise<CoreObservation> {

  const capturedAt =
    new Date().toISOString()

  const controller =
    new AbortController()

  const timer =
    window.setTimeout(
      () => controller.abort(),
      15000,
    )

  try {

    const response =
      await fetch(
        route,
        {
          method: 'GET',

          headers: {
            Accept:
              'application/json',
          },

          credentials:
            'same-origin',

          signal:
            controller.signal,
        },
      )

    const raw =
      await response.text()

    let data: unknown =
      raw

    if (raw) {
      try {
        data =
          JSON.parse(raw)
      } catch {
        data =
          raw
      }
    }

    return {
      route,

      status:
        response.status,

      ok:
        response.ok,

      capturedAt,

      ...(response.ok
        ? {
            data:
              compact(data),
          }
        : {
            error:
              typeof data === 'string'
                ? data.slice(0, 1000)
                : `HTTP ${response.status}`,
          }),
    }

  } catch (error) {

    return {
      route,
      status: 0,
      ok: false,
      capturedAt,

      error:
        error instanceof Error
          ? error.message
          : 'Unknown observability error',
    }

  } finally {

    window.clearTimeout(timer)

  }
}

export async function collectCoreObservability():
  Promise<CoreObservabilitySnapshot> {

  const observations =
    await Promise.all(
      SURFACES.map(
        route =>
          readSurface(route),
      ),
    )

  const successfulSurfaces =
    observations.filter(
      item => item.ok,
    ).length

  return {
    contract:
      'SEI_GENESIS_CORE_OBSERVABILITY_V1',

    source:
      'LIVE_SEI_CORE',

    capturedAt:
      new Date().toISOString(),

    verified:
      successfulSurfaces > 0,

    readOnly:
      true,

    totalSurfaces:
      observations.length,

    successfulSurfaces,

    failedSurfaces:
      observations.length -
      successfulSurfaces,

    observations,
  }
}
